/**
 * Single source of truth for tenant-slug resolution.
 *
 * Used by both `utils/api.js` (Axios interceptor) and `contexts/TenantContext.js`
 * to avoid duplicated logic / drifting reserved-paths lists.
 *
 * Resolution order:
 *   1. localStorage.tenant_slug   (written by TenantContext after a successful fetch)
 *   2. URL path parse             (first segment, unless it's a reserved root route)
 *
 * The URL parse is kept as a fallback for the initial page load before
 * TenantContext has had a chance to populate localStorage.
 */

// Reserved root-level routes that must NEVER be treated as tenant slugs.
// Keep this list in sync with `SocialCrawlerMiddleware.reserved_paths` in
// `backend/server.py` if you add/remove routes.
export const RESERVED_PATHS = Object.freeze([
  'admin',
  'super-admin',
  'signup',
  'register',
  'quote',
  'login',
  'forgot-password',
  'pricing',
  'features',
  'contact',
  'static',
]);

/** Parse the first URL path segment, returning it only if it is not a reserved route. */
export const getTenantSlugFromPath = () => {
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  if (pathParts.length > 0 && !RESERVED_PATHS.includes(pathParts[0])) {
    return pathParts[0];
  }
  return null;
};

/**
 * Get the current tenant slug from the canonical source.
 * Falls back to URL parsing only when localStorage is empty.
 */
export const getTenantSlug = () => {
  const stored = (() => {
    try {
      return localStorage.getItem('tenant_slug');
    } catch {
      return null;
    }
  })();
  if (stored) return stored;
  return getTenantSlugFromPath();
};
